import { DragDropProvider } from "@dnd-kit/react";
import { get, isEqual } from "lodash";
import moment from "moment";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import { DATE_FORMATS } from "../../../constants";
import { AthleteLinks } from "../../../parts/athlete-links";
import BackButton from "../../../parts/BackButton";
import ExerciseClipboardContent from "../../../parts/ExerciseClipboardContent";
import InsertUndoToast from "../../../parts/InsertUndoToast";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import SvgIcon from "../../../parts/SvgIcon";
import ToolTip from "../../../parts/tooltip-portal";
import { RillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";
import MonthGrid from "../../dashboard/calendar/components/MonthGrid";
import { getCalendarMonthInsightsRange } from "../programs/program-insights/utils/inputs/calendarInsights";
import CalendarInsightsSection, {
  CALENDAR_INSIGHTS_SHELL_CLASS,
} from "./components/CalendarInsightsSection";
import CalendarViewToggle from "./components/CalendarViewToggle";
import MetricsDrawer from "./components/MetricsDrawer";
import MonthWeek from "./components/MonthWeek";

const { DATE_YEAR_FORMAT, DATE_DAY_FORMAT } = DATE_FORMATS;

const weeksInMonth = [...Array(6)].map((_, i) => i);

class CalendarMonthView extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      showMetricsDrawer: false,
      selectedDates: [],
      daysInEditMode: [],
      loaded: false,
      scheduledSessions: context.locals.scheduledSessions,
      expanded: true,
      editCalendar: false,
      opened: false,
      insightsShellActive: false,
    };
    this.locals = context.locals;
    this.handleSelectDate = this.handleSelectDate.bind(this);
    this.handleRemoveMultiple = this.handleRemoveMultiple.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.updateWorkout = this.updateWorkout.bind(this);
    this.forceSessionsUpdate = this.forceSessionsUpdate.bind(this);
    this.handleDeselectAll = this.handleDeselectAll.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.toggleMetricsDrawer = this.toggleMetricsDrawer.bind(this);
    this.handleEditDate = this.handleEditDate.bind(this);
    this.handleInsightsShellActiveChange =
      this.handleInsightsShellActiveChange.bind(this);
  }

  /* Function to handle the insights shell active change */
  handleInsightsShellActiveChange(active) {
    this.setState((prev) =>
      prev.insightsShellActive === active
        ? null
        : { insightsShellActive: active }
    );
  }

  handleDrop({ operation }) {
    if (!operation.target) return;

    const sourceId = operation.source.data.id;
    const targetId = operation.target.data.id;

    if (sourceId === targetId) return;

    this.forceSessionsUpdate(sourceId, targetId);
    this.updateWorkout(sourceId, targetId);
  }

  handleEditDate({ type = "add", day = "" }) {
    const result =
      type === "add"
        ? [...this.state.daysInEditMode, day]
        : this.state.daysInEditMode.filter((workout) => workout !== day);
    this.setState({
      daysInEditMode: result,
    });
  }

  updateWorkout(dateFrom, dateTo) {
    const athlete =
      this.props.athleteProp ||
      get(this.locals, "query.athlete") ||
      get(this.locals, "athlete._id");

    submit(
      `/actions/exercise/move-day?proxyAthlete=${athlete}&reqSource=calendar&dnd=true`,
      {
        method: "POST",
        body: {
          from: moment.utc(dateFrom, DATE_DAY_FORMAT).startOf("day").toJSON(),
          to: moment(dateTo, DATE_DAY_FORMAT).startOf("day").toJSON(),
          type: "move",
        },
      }
    );
  }

  forceSessionsUpdate(draggingFrom, draggingTo) {
    const { scheduledSessions } = this.state;

    const tempSessions = { ...scheduledSessions };
    const oldDate = tempSessions[draggingFrom];

    delete tempSessions[draggingFrom];
    tempSessions[draggingTo] = oldDate;

    this.setState({
      scheduledSessions: tempSessions,
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
    const { locals } = nextContext;
    const { scheduledSessions } = locals;
    if (!isEqual(scheduledSessions, this.state.scheduledSessions)) {
      this.setState({ scheduledSessions });
    }
  }

  handleSelectDate(e, dayString) {
    const selectedDates = this.state.selectedDates;
    if (selectedDates.indexOf(dayString) > -1) {
      selectedDates.splice(selectedDates.indexOf(dayString), 1);
    } else {
      selectedDates.push(dayString);
    }

    this.setState({ selectedDates });
  }

  handleDeselectAll() {
    this.setState({
      selectedDates: [],
    });
  }

  handleSelectAll() {
    const { scheduledSessions } = this.context.locals;
    const newSelectedDates = [];
    Object.keys(scheduledSessions).map((key) => {
      newSelectedDates.push(key);
    });

    this.setState({
      selectedDates: newSelectedDates,
    });
  }

  handleRemoveMultiple(e, singleDate = false) {
    e.preventDefault();
    if (confirm(this.props.t("calendarMonth.removeSelectedWorkoutsConfirm"))) {
      const days = this.state.selectedDates.length
        ? this.state.selectedDates.map((date) =>
            moment(date).format(DATE_DAY_FORMAT)
          )
        : [moment(singleDate).format(DATE_DAY_FORMAT)];

      const athlete =
        this.props.athleteProp ||
        get(this.locals, "query.athlete") ||
        get(this.locals, "athlete._id");

      submit(
        `/actions/exercise/unschedule-workout?days=${days}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body: { days },
        }
      );

      this.setState({
        selectedDates: [],
      });
    }
  }

  switchMonth(e, direction, activeMonth) {
    e.preventDefault();
    this.setState({
      loaded: false,
      selectedDates: [],
      // This restores the opacity when changing the month with edit modes opened
      daysInEditMode: [],
    });
    submit(`/actions/refresh?`, {
      method: "GET",
      body: {
        month: activeMonth
          .clone()
          .add(direction, "months")
          .format(DATE_YEAR_FORMAT),
      },
    });
  }

  toggleMetricsDrawer(data = null) {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        metrics: this.context.locals.query.metrics ? false : "outcomes",
      },
    });
    this.setState({
      showMetricsDrawer: data !== null ? data : !this.state.showMetricsDrawer,
    });
  }

  calculateEmptyDayColumns(
    scheduledSessions,
    activeMonth,
    startDay,
    editCalendar
  ) {
    // Only apply dynamic sizing in edit mode
    if (!editCalendar) {
      return null;
    }

    const sessionDates = Object.keys(scheduledSessions);

    // Calculate the visible date range for the calendar grid
    // Start of first week (can be in previous month)
    const startOfMonth = activeMonth.clone().startOf("month");
    const dayOfFirstOfMonth = startOfMonth.day();
    const adjustedStartDay = startDay || 0;

    // Calculate days to go back to reach the starting day of the week
    const daysToGoBack = (dayOfFirstOfMonth - adjustedStartDay + 7) % 7;
    const startOfFirstWeek = startOfMonth
      .clone()
      .subtract(daysToGoBack, "days");

    // End of last week (can be in next month) - could be 5 or 6 weeks shown
    // Week 5 is only shown if it contains days from the active month
    const startOfWeek5 = startOfFirstWeek.clone().add(5, "weeks");
    const showWeek5 = startOfWeek5.month() === activeMonth.month();

    const endOfLastWeek = startOfFirstWeek
      .clone()
      .add(showWeek5 ? 6 : 5, "weeks")
      .subtract(1, "day")
      .endOf("day");

    // Track which day-of-week indices have workouts (0 = starting day, 1 = next day, etc.)
    const daysWithWorkouts = new Set();

    sessionDates.forEach((dateString) => {
      const date = moment.utc(dateString, DATE_DAY_FORMAT);

      // Only consider dates within the visible calendar range
      if (date.isBetween(startOfFirstWeek, endOfLastWeek, null, "[]")) {
        // Calculate day of week index based on the user's starting calendar day
        const dayOfWeek = date.day();
        const adjustedDayIndex = (dayOfWeek - (startDay || 0) + 7) % 7;
        daysWithWorkouts.add(adjustedDayIndex);
      }
    });

    // If 7 days have workouts, no need for dynamic sizing
    if (daysWithWorkouts.size === 7) {
      return null;
    }

    // Create column width array with max-width constraints
    // Grid needs to know about the 27rem max-width to properly distribute space
    const columnWidths = [];
    for (let i = 0; i < 7; i++) {
      if (daysWithWorkouts.has(i)) {
        // Columns with a workout
        columnWidths.push("minmax(20rem, 27rem)");
      } else {
        // Empty columns
        columnWidths.push("15rem");
      }
    }

    return columnWidths;
  }

  render() {
    const { t } = this.props;
    const {
      activeMonth,
      query,
      startOfFirstWeek,
      activeUser,
      activeUserToRequest,
      activeExercises,
      athlete,
      pathname,
      fromMetric,
      organizationOwner,
      clientConfiguration,
      athleteFlexibleProgram,
    } = this.context.locals;

    const startingCalendarDay = activeUser.startingCalendarDay || 0;
    const monthInsightsRange = getCalendarMonthInsightsRange(
      activeMonth,
      startingCalendarDay
    );
    const insightsPeriodKeys = monthInsightsRange.visiblePeriods
      .map((period) => period.periodKey)
      .join("|");
    const athleteId =
      this.props.athleteProp ||
      get(this.locals, "query.athlete") ||
      get(this.locals, "athlete._id") ||
      activeUser._id;
    const flexibleCycle = athleteFlexibleProgram?.[0];
    const calendarReturnHref = `/athlete-portal/calendar?view=month&month=${
      query.month || ""
    }&athlete=${athleteId}`;
    const flexibleProgramHref = `/athlete-portal/programs/flexible-program/${
      flexibleCycle?._id
    }?athlete=${athleteId}${
      query.organizationCoach
        ? `&organizationCoach=${query.organizationCoach}`
        : ""
    }&backTo=${encodeURIComponent(calendarReturnHref)}`;

    const { scheduledSessions, selectedDates, opened } = this.state;
    const totalScheduledSessions = Object.keys(scheduledSessions).length;
    const intensityMeasure =
      get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
        ? "RPE"
        : "RiR";
    const isRiR = intensityMeasure === "RiR";
    const allSelected = totalScheduledSessions === selectedDates.length;

    // Calculate column widths once for the entire month
    const columnWidths = this.calculateEmptyDayColumns(
      scheduledSessions,
      activeMonth,
      activeUser.startingCalendarDay,
      query.editCalendar
    );

    // Calculate dynamic min-width for the section element to avoid dead space
    let sectionMinWidth = null;
    if (columnWidths) {
      const fullColumns = columnWidths.filter(
        (w) => w === "minmax(20rem, 27rem)"
      ).length;
      const emptyColumns = columnWidths.filter((w) => w === "15rem").length;
      // 32px for aside + column widths + gaps (0.5rem between each of 6 gaps)
      sectionMinWidth = `calc(32px + ${fullColumns * 27}rem + ${
        emptyColumns * 15
      }rem + ${6 * 0.5}rem)`;
    }
    const viewTypeOptions = [
      {
        text: t("calendar.training", "Training"),
        value: "training",
        href: `/actions/refresh?view=month&contentType=training&month=${
          this.locals.query.month || ""
        }&week=&date=`,
        IconImg: (
          <SvgIcon src="/img/new-icons/barbell.svg" useCurrentColor={true} />
        ),
      },
      {
        text: t("calendar.plan"),
        value: "plan",
        href: `/actions/refresh?view=month&contentType=plan&month=${
          this.locals.query.month || moment().format(DATE_YEAR_FORMAT)
        }&week=&date=`,
        IconImg: (
          <SvgIcon
            src="/img/new-icons/clipboard-notes.svg"
            useCurrentColor={true}
          />
        ),
      },
      {
        text: t("calendar.log"),
        value: "log",
        href: `/actions/refresh?view=month&contentType=log&month=${
          this.locals.query.month || moment().format(DATE_YEAR_FORMAT)
        }&week=&date=`,
        IconImg: (
          <SvgIcon
            src="/img/new-icons/document-text.svg"
            useCurrentColor={true}
          />
        ),
      },
    ];

    return (
      <main
        className={`Calendar${query.editCalendar ? " edit-modex" : ""}${
          this.state.insightsShellActive
            ? ` ${CALENDAR_INSIGHTS_SHELL_CLASS}`
            : ""
        }`}
      >
        <header>
          <section>
            <hgroup>
              {this.props.showBack && <BackButton />}
              {selectedDates.length > 0 ? (
                <>
                  {totalScheduledSessions > 0 && (
                    <button
                      data-button-type="secondary"
                      onClick={
                        allSelected
                          ? this.handleDeselectAll
                          : this.handleSelectAll
                      }
                    >
                      <SvgIcon
                        className="mr-2"
                        src={
                          allSelected
                            ? "/img/new-icons/checked-square.svg"
                            : "/img/new-icons/unchecked-square.svg"
                        }
                        noFilterOnLight={allSelected}
                      />
                      {allSelected
                        ? t("calendarMonth.deselectAll")
                        : t("calendarMonth.selectAll")}
                    </button>
                  )}
                  <p className="p4">
                    ({selectedDates.length} {t("calendarMonth.selected")})
                  </p>
                </>
              ) : (
                <>
                  <button
                    className="bg-(--dark-grey) pr-2!"
                    onClick={() => {
                      submit(`/actions/refresh/?`, {
                        method: "GET",
                        body: {
                          editCalendar: !query.editCalendar,
                        },
                      });
                    }}
                  >
                    {t("calendarMonth.editMode")}
                    <input
                      type="checkbox"
                      name="editCalendar"
                      id="editCalendar"
                      role="switch"
                      checked={query.editCalendar}
                    />
                    <ToolTip body={t("calendarMonth.editModeTooltip")}>
                      ?
                    </ToolTip>
                  </button>
                </>
              )}
              <CalendarViewToggle
                name="contentType"
                options={viewTypeOptions}
              />
            </hgroup>

            <hgroup>
              <div role="month-handler">
                <div className="bg-(--dark-grey)!">
                  <button
                    data-button-type="text-white"
                    data-icon-only
                    onClick={(e) => this.switchMonth(e, -1, activeMonth)}
                  >
                    <SvgIcon src="/img/new-icons/arrow-left.svg" />
                  </button>
                  <span>{activeMonth.format("MMMM, YYYY")}</span>
                  <button
                    data-button-type="text-white"
                    data-icon-only
                    onClick={(e) => this.switchMonth(e, 1, activeMonth)}
                  >
                    <SvgIcon src="/img/new-icons/arrow-right.svg" />
                  </button>
                </div>
              </div>
            </hgroup>

            <hgroup>
              {selectedDates.length > 0 ? (
                <>
                  <button
                    data-button-type="secondary"
                    onClick={() => this.setState({ selectedDates: [] })}
                  >
                    {t("calendar.cancel")}
                  </button>
                  <button
                    data-button-type="secondary"
                    className="text-(--danger)!"
                    onClick={this.handleRemoveMultiple}
                    disabled={selectedDates.length === 0}
                  >
                    <SvgIcon
                      className="mr-2"
                      src={"/img/new-icons/red-trash.svg"}
                      noFilterOnLight={true}
                    />
                    {t("calendar.remove")}
                  </button>
                </>
              ) : (
                <>
                  <a
                    href={`/actions/refresh?month=${moment().format(
                      DATE_YEAR_FORMAT
                    )}`}
                    data-button-type="outline"
                    role="button"
                  >
                    {t("calendarMonth.thisMonth")}
                  </a>
                  <ToggleLinksInput
                    name="view"
                    className={"view-segmented"}
                    options={[
                      {
                        text: t("calendar.month"),
                        value: "month",
                        href: `/actions/refresh?view=month&month=${
                          query.week
                            ? moment(query.week, DATE_DAY_FORMAT).format(
                                DATE_YEAR_FORMAT
                              )
                            : query.date
                              ? moment(query.date, DATE_DAY_FORMAT).format(
                                  DATE_YEAR_FORMAT
                                )
                              : ""
                        }&week=&date=`,
                      },
                      {
                        text: t("calendar.week"),
                        value: "week",
                        href: `/actions/refresh?view=week&week=${
                          moment().isSame(query.month, "month")
                            ? moment().format(DATE_DAY_FORMAT)
                            : moment(query.month, DATE_YEAR_FORMAT).format(
                                DATE_DAY_FORMAT
                              )
                        }&month=&date=&relTrends=true`,
                      },
                      {
                        text: t("calendar.day"),
                        value: "day",
                        href: `/actions/refresh?view=day&date=${
                          moment().isSame(query.month, "month")
                            ? moment().format(DATE_DAY_FORMAT)
                            : moment(query.month, DATE_YEAR_FORMAT).format(
                                DATE_DAY_FORMAT
                              )
                        }&month=&week=&relTrends=true`,
                      },
                    ]}
                  />
                  <details role="list" open>
                    <summary
                      onClick={(e) => {
                        e.preventDefault();
                        this.setState({ opened: !opened });
                      }}
                      onBlur={(e) => {
                        e.preventDefault();
                        this.setState({ opened: false });
                      }}
                      aria-haspopup="listbox"
                    >
                      {t("calendarMonth.more")}
                      <SvgIcon
                        src={
                          opened
                            ? "/img/svg/angle-up.svg"
                            : "/img/svg/angle-down.svg"
                        }
                      />
                    </summary>
                    <ul role="listbox" className={opened ? "opened" : ""}>
                      <div className="list-title">
                        <span>{t("calendarMonth.settings")}</span>
                        <div />
                      </div>
                      <li
                        onMouseDown={() => {
                          submit("/coaching-portal/settings/preferences");
                        }}
                      >
                        <span>{t("calendarMonth.editCalendarSettings")}</span>
                        <SvgIcon src="/img/new-icons/export.svg" />
                      </li>
                      <li
                        onMouseDown={() => {
                          this.toggleMetricsDrawer();
                        }}
                      >
                        <span>{t("calendarMonth.trainingMetrics")}</span>
                        <SvgIcon src="/img/new-icons/export.svg" />
                      </li>

                      {!query.editCalendar ? (
                        <>
                          <li
                            onMouseDown={() =>
                              this.setState({ expanded: !this.state.expanded })
                            }
                          >
                            <span>{t("calendarMonth.expandCalendar")}</span>
                            <SvgIcon
                              src={
                                this.state.expanded
                                  ? "/img/new-icons/checked-square.svg"
                                  : "/img/new-icons/unchecked-square.svg"
                              }
                              noFilterOnLight={this.state.expanded}
                              classNames="mr-[-0.15rem]"
                            />
                          </li>

                          <div className="list-title mt-3">
                            <span>{t("calendarMonth.legend")}</span>
                            <div />
                          </div>
                          <div className="legend-item">
                            <div className="circle-status completed" />
                            {t("calendarMonth.fullyCompleted")}
                          </div>
                          <div className="legend-item">
                            <div className="circle-status partial" />
                            {t("calendarMonth.partiallyComplete")}
                          </div>
                          <div className="legend-item">
                            <div className="circle-status target" />
                            {t("calendarMonth.target")}
                          </div>
                        </>
                      ) : null}
                    </ul>
                  </details>
                </>
              )}
            </hgroup>
          </section>
          {flexibleCycle && (
            <div className="flexible-calendar-chip-row">
              <a className="flexible-calendar-chip" href={flexibleProgramHref}>
                <SvgIcon src="/img/new-icons/bolt.svg" useCurrentColor={true} />
                {t("calendarMonth.flexibleActive", { name: athlete.name })}
                <SvgIcon
                  src="/img/new-icons/settings.svg"
                  useCurrentColor={true}
                />
              </a>
            </div>
          )}
        </header>
        <div className="athlete-section-header only-show-mobile">
          <AthleteLinks
            athlete={athlete}
            pathName={pathname}
            classNames={"only-show-mobile"}
            query={query}
          />
          <div>
            <div className="athlete-select dropdown-athlete">
              <div
                className="avatar"
                style={{
                  backgroundImage: `url(${getAvatarUrl(athlete?._id)})`,
                }}
              />
              <AthleteSelect locals={this.context.locals} />
            </div>
            <ToggleLinksInput
              name="view"
              defaultActive={
                query.contentType === "plan" || query.contentType === "log"
                  ? query.contentType
                  : query.view
              }
              options={[
                {
                  text: t("calendar.month"),
                  value: "month",
                  href: `/actions/refresh?view=month&contentType=training&month=${
                    query.week
                      ? moment(query.week, DATE_DAY_FORMAT).format(
                          DATE_YEAR_FORMAT
                        )
                      : query.date
                        ? moment(query.date, DATE_DAY_FORMAT).format(
                            DATE_YEAR_FORMAT
                          )
                        : ""
                  }&week=&date=`,
                },

                {
                  text: t("calendar.day"),
                  value: "day",
                  href: `/actions/refresh?view=day&contentType=training&date=${
                    moment().isSame(query.month, "month")
                      ? moment().format(DATE_DAY_FORMAT)
                      : moment(query.month, DATE_YEAR_FORMAT).format(
                          DATE_DAY_FORMAT
                        )
                  }&month=&week=`,
                },
                {
                  text: t("calendar.plan"),
                  value: "plan",
                  href: `/actions/refresh?view=month&contentType=plan&month=${
                    query.month || moment().format(DATE_YEAR_FORMAT)
                  }&week=&date=`,
                },
                {
                  text: t("calendar.log"),
                  value: "log",
                  href: `/actions/refresh?view=month&contentType=log&month=${
                    query.month || moment().format(DATE_YEAR_FORMAT)
                  }&week=&date=`,
                },
              ]}
            />
          </div>
        </div>

        <DragDropProvider onDragEnd={this.handleDrop}>
          <CalendarInsightsSection
            scope="calendar-month"
            enabled={!query.metrics}
            from={monthInsightsRange.from}
            to={monthInsightsRange.to}
            periodKeys={insightsPeriodKeys}
            visiblePeriods={monthInsightsRange.visiblePeriods}
            activeExercises={activeExercises}
            athleteId={athleteId}
            startingCalendarDay={startingCalendarDay}
            activeUser={activeUser}
            activeUserToRequest={activeUserToRequest}
            organizationOwner={organizationOwner}
            clientConfiguration={clientConfiguration}
            fromMetric={fromMetric}
            onInsightsShellActiveChange={this.handleInsightsShellActiveChange}
          >
            {(monthGridScrollRef) => (
              <MonthGrid
                ref={monthGridScrollRef}
                editCalendar={query.editCalendar}
                startDay={startingCalendarDay}
                columnWidths={columnWidths}
                sectionMinWidth={sectionMinWidth}
              >
                {weeksInMonth.map((week) => {
                  const startOfWeek = startOfFirstWeek
                    .clone()
                    .add(week, "weeks");
                  if (
                    !(
                      week === 5 &&
                      startOfWeek.clone().month() !==
                        activeMonth.clone().month()
                    )
                  ) {
                    return (
                      <MonthWeek
                        activeMonth={activeMonth.clone()}
                        key={week}
                        startOfWeek={startOfWeek}
                        handleSelectDate={this.handleSelectDate}
                        selectedDates={this.state.selectedDates}
                        scheduledSessions={this.state.scheduledSessions}
                        handleRemoveMultiple={this.handleRemoveMultiple}
                        expanded={this.state.expanded}
                        editCalendar={query.editCalendar}
                        handleEditDate={this.handleEditDate}
                        daysInEditMode={this.state.daysInEditMode}
                        removing={selectedDates.length > 0}
                        columnWidths={columnWidths}
                      />
                    );
                  }
                  return null;
                })}

                {this.state.scheduledSessions &&
                  Object.keys(this.state.scheduledSessions).length <= 0 && (
                    <div className="no-workouts-message only-show-mobile">
                      <span>{t("calendarMonth.noWorkoutFound")}</span>
                      <span>
                        {t("calendarMonth.noWorkoutsScheduled", {
                          month: moment(activeMonth).format("MMMM"),
                        })}
                      </span>
                    </div>
                  )}
              </MonthGrid>
            )}
          </CalendarInsightsSection>
        </DragDropProvider>
        {query.metrics ? (
          <MetricsDrawer
            exerciseSelected={query.exercise}
            toggleMetricsDrawer={this.toggleMetricsDrawer}
            calendarMetrics={this.context.locals.calendarMetrics}
            type={query.metrics}
            userUnit={activeUser.units}
            intensityMeasure={intensityMeasure}
            isRiR={isRiR}
          />
        ) : null}
        <ExerciseClipboardContent
          query={query.exercisesInClipboard}
          locals={this.context.locals}
        />
        <InsertUndoToast
          athleteId={
            get(this.locals, "query.athlete") ||
            get(this.locals, "athlete._id") ||
            get(this.locals, "activeUser._id")
          }
        />
      </main>
    );
  }
}

CalendarMonthView.contextType = RillContext;

export default withTranslation("athlete-portal")(CalendarMonthView);
